import { Analytics } from '@aws-amplify/analytics';
import { AnalyticsEvent, EventAttributes } from '@aws-amplify/analytics/lib-esm/types';
import { Auth } from '@aws-amplify/auth';
import { ClientDevice } from '@aws-amplify/core';
import katalTemplateString from 'src/helpers/katalHelper';
import Config from 'src/analytics/dev-config';
import Attribute from 'src/constants/attribute';
import CookieHelper from 'src/helpers/cookieHelper';
import CookiesConstants from 'src/constants/cookies';
import { kinesisAnalyticsEnabled } from 'src/constants/feature-gates';
import { buildEnvelope, EnvelopeDevice } from 'src/analytics/envelope';
import { getSession, resetSession } from 'src/analytics/session';
import { getClientId, getCognitoId, resetClientIdentity, setCognitoId } from 'src/analytics/clientIdentity';
import { configureTransport, resetTransport, sendEnvelope } from 'src/analytics/transport';

const COGNITO_IDENTITY_POOL_ID = katalTemplateString('{{CognitoIdentityPoolId}}', Config.COGNITO_IDENTITY_POOL_ID_DEV);
const PINPOINT_APP_ID = katalTemplateString('{{PinpointAppId}}', Config.PINPOINT_APP_ID_DEV);

const authConfig = {
    Auth: {
        identityPoolId: COGNITO_IDENTITY_POOL_ID,
        region: 'us-west-2',
    }
}

const analyticsConfig = {
    AWSPinpoint: {
        appId: PINPOINT_APP_ID,
        region: 'us-west-2',
        mandatorySignIn: false,
    }
}

const pageViewConfig = {
    enable: true,
    eventName: 'pageView',
    type: 'SPA',
    attributes: {
      referrer: document.referrer,
    },
    provider: 'AWSPinpoint',
    getUrl: () => window.location.href.toString(),
  };

// StrictMode double-invokes componentDidMount. configureTransport carries its own guard, but a
// second autoTrack('pageView') re-enters _pageViewTrackSPA and removes the history patches.
let initialized = false;
let identityResolved = false;
const queued: { event: AnalyticsEvent; now: number }[] = [];

function initializeAmplify(): void {
    if (initialized) {
        return;
    }
    initialized = true;

    Auth.configure(authConfig);

    if (kinesisAnalyticsEnabled()) {
        configureTransport();
        resolveIdentity();
    } else {
        Analytics.configure(analyticsConfig);
        Analytics.autoTrack('pageView', pageViewConfig);
    }

    Analytics.enable();
}

export function resetAnalytics(): void {
    initialized = false;
    identityResolved = false;
    queued.length = 0;
    resetTransport();
    resetSession();
    resetClientIdentity();
}

// Nothing sends until the identity id lands: cognito_id is populated on every record today, and
// screen-visit events fire on mount, before Cognito's round trip completes. Queued events keep the
// clock from when they were recorded, so only delivery is deferred.
function resolveIdentity(): void {
    Auth.currentCredentials()
        .then((credentials) => setCognitoId(credentials.identityId))
        .catch(() => undefined)
        .then(() => {
            identityResolved = true;
            queued.forEach((entry) => send(entry.event, entry.now));
            queued.length = 0;
        })
        .catch(() => undefined);
}

export function recordEvent(event: AnalyticsEvent): void {
    event.attributes = enrichFromCookies(event.attributes || {}, event.name);
    dispatch(event);
}

function dispatch(event: AnalyticsEvent): void {
    if (!kinesisAnalyticsEnabled()) {
        Analytics.record(event);
        return;
    }

    const now = Date.now();
    if (!identityResolved) {
        queued.push({ event, now });
        return;
    }

    send(event, now);
}

function send(event: AnalyticsEvent, now: number): void {
    sendEnvelope(buildEnvelope({
        eventType: event.name,
        attributes: event.attributes || {},
        session: getSession(now),
        client: { clientId: getClientId(), cognitoId: getCognitoId() },
        device: deviceInfo(),
        now,
    }), event.immediate ?? false);
}

function enrichFromCookies(attributes: EventAttributes, eventName: string): EventAttributes {
    const attributeCookieMap = new Map<string, string>();
    attributeCookieMap.set(Attribute.PERSON_ID, CookieHelper.readCookie(CookiesConstants.PERSON_ID));
    attributeCookieMap.set(Attribute.USERNAME, CookieHelper.readCookie(CookiesConstants.USERNAME));
    attributeCookieMap.set(Attribute.REFERRER, CookieHelper.readCookie(CookiesConstants.REFERRER));
    attributeCookieMap.set(Attribute.SESSION_ID, CookieHelper.readCookie(CookiesConstants.SESSION_ID));

    // Only track previous page for screens since the value would change nondeterministically in children components
    if (eventName.includes('Screen')) {
        attributeCookieMap.set(Attribute.PREVIOUS_PAGE, CookieHelper.readCookie(CookiesConstants.PREVIOUS_PAGE));
    }

    return setAttributesFromMap(attributes, attributeCookieMap);
}

function deviceInfo(): EnvelopeDevice {
    const info = ClientDevice.clientInfo();
    return {
        make: info.make ?? '',
        model: info.model ?? '',
        platformName: info.platform ?? '',
    };
}

function setAttributesFromMap(attributes: EventAttributes, attributeCookieMap: Map<string, string>) {
    attributeCookieMap.forEach((value, key) => {
        // we will ignore empty string ('') values in the map when setting the attributes
        if (value) {
            attributes[key] = value;
        }
    } );
    return attributes;
}

export default initializeAmplify;
